/** "Who gets support alerts" — tick Text / Email per staff member, fix mobiles inline, send yourself a test text. */
import React, { useContext, useEffect, useState } from 'react';
import { AlertTriangle, BellRing, CheckCircle2, Loader2, Mail, MessageCircle, Send } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { formatApiError } from '@/utils/apiError';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export function SupportAlertsModal({ onClose, onSaved }) {
  const { user } = useContext(AuthContext);
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/support/alert-recipients`, { credentials: 'include' }).then((r) => r.json()).then((d) => setRows((d.staff || []).map((r) => ({ ...r, orig_phone: r.phone })))).catch(() => toast.error('Could not load staff'));
  }, []);

  const update = (uid, patch) => setRows((cur) => cur.map((r) => (r.user_id === uid ? { ...r, ...patch } : r)));
  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/support/alert-recipients`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipients: rows.map((r) => ({ user_id: r.user_id, sms: r.sms, email: r.email_on, phone: r.phone !== r.orig_phone ? r.phone : undefined })) }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(d) || 'Save failed');
      setRows(d.staff.map((r) => ({ ...r, orig_phone: r.phone })));
      toast.success('Support alert recipients saved');
      onSaved?.(d);
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  const testText = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${API_URL}/api/support/alert-recipients/test-text`, { method: 'POST', credentials: 'include' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(d) || 'Test failed');
      toast.success(`Test text sent to ${d.to}`);
    } catch (e) { toast.error(e.message); } finally { setTesting(false); }
  };

  const smsCount = rows?.filter((r) => r.sms && r.phone_ok).length || 0;
  const emailCount = rows?.filter((r) => r.email_on && r.email).length || 0;
  const me = rows?.find((r) => r.user_id === user?.user_id);
  const myReason = me && (!me.sms ? 'your Text box below isn\'t ticked' : !me.phone_ok ? 'there\'s no valid UK mobile saved against your name' : null);
  const [optPhone, setOptPhone] = useState('');
  const [opting, setOpting] = useState(false);
  const textMe = async () => {
    setOpting(true);
    try {
      const res = await fetch(`${API_URL}/api/support/alert-recipients/text-me`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ phone: optPhone.trim() || undefined }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(d) || 'Could not set that up');
      setRows(d.staff.map((r) => ({ ...r, orig_phone: r.phone })));
      toast.success(d.confirmation_sent ? `Done — confirmation text sent to ${d.to}` : `Done — you're on the list (${d.to}), but the confirmation text failed`);
      onSaved?.(d);
    } catch (e) { toast.error(e.message); } finally { setOpting(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="support-alerts-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BellRing className="w-5 h-5" /> Who gets support alerts</DialogTitle>
          <DialogDescription>Ticked people get a text and/or email for <b>every</b> new support query (including urgent ones) and every note added — whoever it's assigned to. Texts need a UK mobile.</DialogDescription>
        </DialogHeader>
        {me && (
          <div className={`rounded-lg border px-3 py-2 text-sm flex items-start gap-2 ${myReason ? 'bg-amber-50 border-amber-300 text-amber-900' : 'bg-emerald-50 border-emerald-300 text-emerald-900'}`} data-testid="support-alert-my-status">
            {myReason ? <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> : <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />}
            <div className="flex-1">{myReason ? <><b>You won't get a text for new tickets</b> — {myReason}.</> : <><b>You're set up</b> — new tickets and notes will text {me.phone}.</>}
              {myReason && (
                <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="support-alert-text-me-row">
                  {!me.phone_ok && <input value={optPhone} onChange={(e) => setOptPhone(e.target.value)} placeholder="Your mobile 07…" inputMode="tel" className="h-9 w-40 text-sm border border-amber-300 rounded px-2 bg-white" data-testid="support-alert-text-me-phone" />}
                  <Button size="sm" onClick={textMe} disabled={opting || (!me.phone_ok && optPhone.replace(/[\s\-()]/g, '').length < 10)} className="bg-slate-900 text-white font-bold h-9" data-testid="support-alert-text-me">
                    {opting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <MessageCircle className="w-3.5 h-3.5 mr-1" />} Text me every new ticket
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
        {rows && smsCount === 0 && (
          <p className="text-xs text-rose-700 font-semibold" data-testid="support-alert-nobody">Nobody is set to be texted right now — new tickets only reach people ticked here (with a UK mobile).</p>
        )}
        {!rows ? <div className="py-8 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                <tr><th className="text-left px-3 py-2">Staff</th><th className="text-left px-3 py-2">Mobile</th><th className="px-3 py-2"><span className="inline-flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5" /> Text</span></th><th className="px-3 py-2"><span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Email</span></th></tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => (
                  <tr key={r.user_id} data-testid={`support-alert-row-${r.user_id}`}>
                    <td className="px-3 py-2"><div className="font-semibold text-gray-900">{r.name}</div><div className="text-[11px] text-gray-500">{r.role} · {r.email || 'no email'}</div></td>
                    <td className="px-3 py-2">
                      <input value={r.phone} onChange={(e) => update(r.user_id, { phone: e.target.value, phone_ok: /^(?:\+44|0)7\d{9}$/.test(e.target.value.replace(/[\s\-()]/g, '')) })} placeholder="07…" disabled={saving} className={`h-8 w-36 text-sm border rounded px-2 ${r.phone && !r.phone_ok ? 'border-rose-400 bg-rose-50' : 'border-gray-300'}`} data-testid={`support-alert-phone-${r.user_id}`} />
                      {r.sms && !r.phone_ok && <div className="text-[10px] text-rose-600 mt-0.5">Needs a UK mobile to get texts</div>}
                    </td>
                    <td className="px-3 py-2 text-center"><input type="checkbox" checked={r.sms} disabled={saving} onChange={(e) => update(r.user_id, { sms: e.target.checked })} className="w-5 h-5 accent-emerald-600" data-testid={`support-alert-sms-${r.user_id}`} /></td>
                    <td className="px-3 py-2 text-center"><input type="checkbox" checked={r.email_on} onChange={(e) => update(r.user_id, { email_on: e.target.checked })} className="w-5 h-5 accent-sky-600" disabled={!r.email || saving} data-testid={`support-alert-email-${r.user_id}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <span className="text-xs text-gray-500" data-testid="support-alert-summary">{smsCount} will be texted · {emailCount} will be emailed</span>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={testText} disabled={testing} data-testid="support-alert-test-text">{testing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />} Text me a test</Button>
            <Button size="sm" onClick={save} disabled={saving || !rows} className="bg-slate-900 text-white font-bold" data-testid="support-alert-save">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}</Button>
          </div>
        </div>
        <p className="text-[11px] text-gray-400">"Text me a test" uses the mobile saved against your name above (save first if you've just changed it).</p>
      </DialogContent>
    </Dialog>
  );
}
